import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Plus, Trash, PencilSimple, Play, ArrowClockwise, CheckCircle,
  XCircle, PaperPlaneTilt, Copy, Check, Clock, LinkSimple, Warning,
} from "@phosphor-icons/react";

// ---------- Small utilities ----------
function IconBtn({ onClick, children, testid, title, danger, className = "" }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`w-8 h-8 flex items-center justify-center border border-border hover:bg-secondary transition-colors ${danger ? "hover:border-[#E00000] hover:text-[#E00000]" : ""} ${className}`}
    >
      {children}
    </button>
  );
}

function CopyInline({ text }) {
  const [c, setC] = useState(false);
  return (
    <button
      onClick={async () => {
        try { await navigator.clipboard.writeText(text); } catch { /* ignore */ }
        setC(true); setTimeout(() => setC(false), 1200);
      }}
      className="p-1 hover:bg-secondary" title="Copy"
    >
      {c ? <Check size={12} className="text-[#008A00]" /> : <Copy size={12} />}
    </button>
  );
}

// ---------- Modal shell ----------
function Modal({ open, onClose, title, children, width = "max-w-3xl" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className={`bg-white border border-border ${width} w-full max-h-[92vh] overflow-hidden flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold tracking-tight">{title}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

// ---------- Field mapping editor ----------
function FieldMappingEditor({ mapping, onChange }) {
  const rows = Object.entries(mapping || {});
  const update = (idx, field, val) => {
    const next = [...rows];
    next[idx] = field === "src" ? [val, next[idx][1]] : [next[idx][0], val];
    onChange(Object.fromEntries(next));
  };
  const addRow = () => onChange({ ...mapping, "": "" });
  const remove = (idx) => {
    const next = rows.filter((_, i) => i !== idx);
    onChange(Object.fromEntries(next));
  };
  return (
    <div className="border border-border">
      <div className="px-4 py-2 bg-secondary border-b border-border grid grid-cols-[1fr_1fr_36px] gap-2 text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">
        <span>CRM field (source)</span>
        <span>External key name (target)</span>
        <span />
      </div>
      <div className="max-h-72 overflow-y-auto divide-y divide-border">
        {rows.length === 0 && (
          <div className="px-4 py-6 text-xs text-muted-foreground text-center">
            No fields mapped. Click &quot;+ Add row&quot; to add one.
          </div>
        )}
        {rows.map(([src, dst], idx) => (
          <div key={idx} className="px-2 py-1.5 grid grid-cols-[1fr_1fr_36px] gap-2 items-center">
            <input
              value={src}
              onChange={(e) => update(idx, "src", e.target.value)}
              placeholder="e.g. customer_name or metadata.gclid"
              className="text-xs font-mono border border-border px-2 py-1.5 focus:border-primary focus:outline-none"
            />
            <input
              value={dst}
              onChange={(e) => update(idx, "dst", e.target.value)}
              placeholder="e.g. name"
              className="text-xs font-mono border border-border px-2 py-1.5 focus:border-primary focus:outline-none"
            />
            <IconBtn onClick={() => remove(idx)} title="Remove" danger>
              <Trash size={14} />
            </IconBtn>
          </div>
        ))}
      </div>
      <div className="px-4 py-2 border-t border-border">
        <button
          onClick={addRow}
          className="text-[10px] uppercase tracking-[0.15em] font-bold text-primary hover:underline flex items-center gap-1"
        >
          <Plus size={12} /> Add row
        </button>
      </div>
    </div>
  );
}

// ---------- Events + Filters selector ----------
const EVENT_LABEL = {
  "lead.created": "New lead created",
  "lead.status_updated": "Status changed",
  "lead.assigned": "Agent assigned",
  "lead.converted": "Converted",
  "lead.lost": "Lost",
  "lead.commented": "Comment added",
  "lead.attributed": "Campaign attributed",
};

function EventsPicker({ selected, allEvents, onChange }) {
  const toggle = (e) => {
    onChange(selected.includes(e) ? selected.filter((x) => x !== e) : [...selected, e]);
  };
  return (
    <div className="grid grid-cols-2 gap-2">
      {allEvents.map((e) => (
        <label
          key={e}
          className={`flex items-center gap-2 px-3 py-2 border cursor-pointer text-xs ${selected.includes(e) ? "border-primary bg-primary/5" : "border-border hover:bg-secondary"}`}
        >
          <input
            type="checkbox" checked={selected.includes(e)} onChange={() => toggle(e)}
            className="accent-primary"
          />
          <div className="flex-1">
            <div className="font-semibold">{EVENT_LABEL[e] || e}</div>
            <div className="font-mono text-[10px] text-muted-foreground">{e}</div>
          </div>
        </label>
      ))}
    </div>
  );
}

// ---------- Create / Edit modal ----------
const EMPTY = {
  name: "",
  url: "",
  events: ["lead.created", "lead.status_updated"],
  active: true,
  field_mapping: {},
  static_fields: {},
  phone_prefix: "",
  headers: {},
  preset: "",
};

function WebhookModal({ open, onClose, editing, allEvents, presets, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [staticFieldsText, setStaticFieldsText] = useState("");
  const [headersText, setHeadersText] = useState("");
  const [creds, setCreds] = useState([]);

  // Load available credentials for the "Use saved credential" quick-pick
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/credentials");
        setCreds(data || []);
      } catch { /* ignore — vault might be empty */ }
    })();
  }, [open]);

  useEffect(() => {
    if (open) {
      const src = editing || EMPTY;
      setForm({
        ...EMPTY, ...src,
        field_mapping: src.field_mapping || {},
        static_fields: src.static_fields || {},
        headers: src.headers || {},
      });
      setStaticFieldsText(JSON.stringify(src.static_fields || {}, null, 2));
      setHeadersText(JSON.stringify(src.headers || {}, null, 2));
    }
  }, [open, editing]);

  const applyPreset = async (key) => {
    if (!key) { setForm((f) => ({ ...f, preset: "" })); return; }
    try {
      const { data } = await api.get(`/webhooks-out/presets/${key}`);
      setForm((f) => ({
        ...f,
        preset: key,
        field_mapping: data.field_mapping || {},
        static_fields: data.static_fields || {},
        phone_prefix: data.phone_prefix || "",
      }));
      setStaticFieldsText(JSON.stringify(data.static_fields || {}, null, 2));
      toast.success(`Applied ${data.name} preset`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const applyCredential = async (credId) => {
    if (!credId) return;
    const c = creds.find((x) => x.id === credId);
    if (!c) return;
    try {
      const { data } = await api.get(`/credentials/${credId}?reveal=true`);
      const value = data.value || "";
      if (c.kind === "webhook_url") {
        setForm((f) => ({ ...f, url: value }));
        toast.success(`URL set from "${c.name}"`);
      } else if (c.kind === "bearer_token") {
        const nextHeaders = { ...(form.headers || {}), Authorization: `Bearer ${value}` };
        setForm((f) => ({ ...f, headers: nextHeaders }));
        setHeadersText(JSON.stringify(nextHeaders, null, 2));
        toast.success(`Bearer token applied from "${c.name}"`);
      } else if (c.kind === "api_key") {
        const nextHeaders = { ...(form.headers || {}), "X-API-Key": value };
        setForm((f) => ({ ...f, headers: nextHeaders }));
        setHeadersText(JSON.stringify(nextHeaders, null, 2));
        toast.success(`API key applied as X-API-Key from "${c.name}"`);
      } else {
        // Copy to clipboard as fallback
        try { await navigator.clipboard.writeText(value); } catch { /* ignore */ }
        toast.message(`Copied "${c.name}" to clipboard — paste where needed`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    if (!form.name.trim() || !form.url.trim()) {
      toast.error("Name and URL are required");
      return;
    }
    if (form.events.length === 0) {
      toast.error("Pick at least one event");
      return;
    }
    let staticFields = {};
    let headers = {};
    try {
      staticFields = staticFieldsText.trim() ? JSON.parse(staticFieldsText) : {};
    } catch { toast.error("Static fields JSON is invalid"); return; }
    try {
      headers = headersText.trim() ? JSON.parse(headersText) : {};
    } catch { toast.error("Headers JSON is invalid"); return; }

    // Clean up field_mapping: drop empty keys/values
    const cleanMapping = Object.fromEntries(
      Object.entries(form.field_mapping).filter(([k, v]) => k.trim() && v.trim())
    );
    const payload = {
      name: form.name.trim(),
      url: form.url.trim(),
      events: form.events,
      active: form.active,
      field_mapping: cleanMapping,
      static_fields: staticFields,
      phone_prefix: form.phone_prefix,
      headers: headers,
      preset: form.preset || null,
    };

    setBusy(true);
    try {
      if (editing?.id) {
        await api.patch(`/webhooks-out/${editing.id}`, payload);
        toast.success("Webhook updated");
      } else {
        await api.post("/webhooks-out", payload);
        toast.success("Webhook created");
      }
      onSaved?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <Modal open={open} onClose={onClose} title={editing ? "Edit outbound webhook" : "New outbound webhook"} width="max-w-4xl">
      <div className="p-6 space-y-6">
        {/* Preset */}
        {!editing && (
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Preset (optional)</label>
            <div className="mt-2 flex gap-2 flex-wrap">
              {Object.entries(presets || {}).map(([key, meta]) => (
                <button
                  key={key}
                  onClick={() => applyPreset(key)}
                  data-testid={`preset-${key}`}
                  className={`px-3 py-1.5 text-xs border ${form.preset === key ? "border-primary bg-primary/5 font-semibold" : "border-border hover:bg-secondary"}`}
                >
                  {meta.name}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">
              Pre-fills field mapping + phone prefix for the tool. You can still customize below.
            </p>
          </div>
        )}

        {/* Basics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Name</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="EasyInsights · New Lead"
              data-testid="webhook-name-input"
              className="w-full mt-1 border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Phone prefix</label>
            <input
              value={form.phone_prefix}
              onChange={(e) => setForm({ ...form, phone_prefix: e.target.value })}
              placeholder="91"
              className="w-full mt-1 border border-border px-3 py-2 text-sm font-mono focus:border-primary focus:outline-none"
            />
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Target URL (https://…)</label>
          <input
            value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            placeholder="https://9e3ff13af60ae62e.easyinsights.in/new_lead/"
            data-testid="webhook-url-input"
            className="w-full mt-1 border border-border px-3 py-2 text-sm font-mono focus:border-primary focus:outline-none"
          />
          {creds.length > 0 && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">Use saved credential →</span>
              <select
                value=""
                onChange={(e) => applyCredential(e.target.value)}
                data-testid="use-credential-select"
                className="text-xs border border-border px-2 py-1 bg-white focus:border-primary focus:outline-none"
              >
                <option value="">Pick from vault…</option>
                {creds.filter((c) => c.active !== false).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.service}/{c.kind} · {c.name}
                  </option>
                ))}
              </select>
              <span className="text-[10px] text-muted-foreground">
                webhook_url → fills URL · bearer_token → Authorization header · api_key → X-API-Key
              </span>
            </div>
          )}
        </div>

        {/* Events */}
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground block mb-2">Events to send</label>
          <EventsPicker
            selected={form.events}
            allEvents={allEvents}
            onChange={(events) => setForm({ ...form, events })}
          />
        </div>

        {/* Field mapping */}
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground block mb-2">
            Field mapping <span className="normal-case tracking-normal text-muted-foreground">— which CRM field goes to which key in the target payload</span>
          </label>
          <FieldMappingEditor
            mapping={form.field_mapping}
            onChange={(field_mapping) => setForm({ ...form, field_mapping })}
          />
        </div>

        {/* Advanced */}
        <details className="border border-border">
          <summary className="px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground bg-secondary cursor-pointer">
            Advanced · static fields, custom headers
          </summary>
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">Static fields (JSON)</label>
              <textarea
                value={staticFieldsText}
                onChange={(e) => setStaticFieldsText(e.target.value)}
                rows={6}
                placeholder={'{ "entity_type": "lead" }'}
                className="w-full mt-1 border border-border px-3 py-2 text-xs font-mono focus:border-primary focus:outline-none"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Constants added to every payload.</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">Custom headers (JSON)</label>
              <textarea
                value={headersText}
                onChange={(e) => setHeadersText(e.target.value)}
                rows={6}
                placeholder={'{ "Authorization": "Bearer XXX" }'}
                className="w-full mt-1 border border-border px-3 py-2 text-xs font-mono focus:border-primary focus:outline-none"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Sent with every request. Use for API tokens.</p>
            </div>
          </div>
        </details>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox" checked={form.active}
            onChange={(e) => setForm({ ...form, active: e.target.checked })}
            className="accent-primary"
          />
          Active (fire events immediately)
        </label>
      </div>

      <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-2">
        <button
          onClick={onClose}
          className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold border border-border hover:bg-secondary"
        >Cancel</button>
        <button
          onClick={save}
          disabled={busy}
          data-testid="webhook-save-button"
          className="px-5 py-2 text-xs uppercase tracking-[0.15em] font-bold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >{busy ? "Saving…" : editing ? "Save changes" : "Create webhook"}</button>
      </div>
    </Modal>
  );
}

// ---------- Deliveries modal ----------
function DeliveriesModal({ open, onClose, hook }) {
  return (
    <Modal open={open} onClose={onClose} title={`Deliveries · ${hook?.name || ""}`} width="max-w-4xl">
      <div className="p-6">
        {(!hook?.recent_deliveries || hook.recent_deliveries.length === 0) && (
          <div className="text-sm text-muted-foreground text-center py-10">No deliveries yet.</div>
        )}
        {hook?.recent_deliveries?.length > 0 && (
          <div className="border border-border">
            <div className="px-4 py-2 bg-secondary border-b border-border grid grid-cols-[80px_1fr_140px_60px_80px] gap-2 text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">
              <span>Status</span><span>Event</span><span>Time</span><span>Try</span><span>Duration</span>
            </div>
            <div className="divide-y divide-border">
              {hook.recent_deliveries.map((d, i) => (
                <div key={i} className="px-4 py-2 grid grid-cols-[80px_1fr_140px_60px_80px] gap-2 text-xs items-center">
                  <span className="flex items-center gap-1">
                    {d.success
                      ? <CheckCircle size={14} className="text-[#008A00]" weight="fill" />
                      : <XCircle size={14} className="text-[#E00000]" weight="fill" />}
                    <span className="font-mono tabular-nums">{d.status_code || "—"}</span>
                  </span>
                  <span className="font-mono truncate">{d.event}</span>
                  <span className="text-muted-foreground tabular-nums">{new Date(d.at).toLocaleString()}</span>
                  <span className="tabular-nums">#{d.attempt}</span>
                  <span className="tabular-nums">{d.duration_ms}ms</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ---------- Main component ----------
export default function OutboundWebhooks() {
  const [hooks, setHooks] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [presets, setPresets] = useState({});
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deliveriesHook, setDeliveriesHook] = useState(null);

  const load = async () => {
    try {
      const [list, evs] = await Promise.all([
        api.get("/webhooks-out"),
        api.get("/webhooks-out/events"),
      ]);
      setHooks(list.data || []);
      setAllEvents(evs.data.events || []);
      setPresets(evs.data.presets || {});
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (h) => {
    try {
      await api.patch(`/webhooks-out/${h.id}`, { active: !h.active });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (h) => {
    if (!confirm(`Delete "${h.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/webhooks-out/${h.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const test = async (h, event) => {
    const ev = event || h.events[0];
    toast.message(`Sending test ${ev} to ${h.name}…`);
    try {
      const { data } = await api.post(`/webhooks-out/${h.id}/test?event=${encodeURIComponent(ev)}`);
      if (data.ok) toast.success(`✓ ${data.status_code} · Delivered`);
      else toast.error(`✗ ${data.status_code || "transport error"} · ${(data.response || "").slice(0, 200)}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const showDeliveries = async (h) => {
    try {
      const { data } = await api.get(`/webhooks-out/${h.id}`);
      setDeliveriesHook(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="border border-border bg-white" data-testid="outbound-webhooks-section">
      <div className="px-5 py-3 border-b border-border flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Outgoing · Send events to other tools</div>
          <h3 className="font-display text-lg font-semibold tracking-tight flex items-center gap-2 mt-0.5">
            <PaperPlaneTilt size={16} weight="fill" className="text-primary" />
            Outbound webhooks
          </h3>
        </div>
        <button
          onClick={() => { setEditing(null); setModalOpen(true); }}
          data-testid="new-outbound-webhook-button"
          className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-1.5"
        >
          <Plus size={14} weight="bold" /> New webhook
        </button>
      </div>

      {loading && <div className="px-5 py-8 text-xs text-muted-foreground text-center">Loading…</div>}

      {!loading && hooks.length === 0 && (
        <div className="px-5 py-10 text-center">
          <PaperPlaneTilt size={32} className="mx-auto text-muted-foreground/50" />
          <p className="text-sm font-semibold mt-3">No outbound webhooks yet</p>
          <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
            When a new lead is created or status is updated, this CRM will POST to your configured URLs (EasyInsights, Zapier, Slack, custom apps…).
          </p>
        </div>
      )}

      {!loading && hooks.length > 0 && (
        <div className="divide-y divide-border">
          {hooks.map((h) => (
            <div key={h.id} className="px-5 py-4" data-testid={`outbound-webhook-${h.id}`}>
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{h.name}</span>
                    {h.preset && (
                      <span className="text-[9px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 bg-primary/10 text-primary border border-primary/20">
                        {h.preset}
                      </span>
                    )}
                    <button
                      onClick={() => toggleActive(h)}
                      className={`text-[9px] uppercase tracking-[0.15em] font-bold px-2 py-0.5 border ${h.active ? "border-[#008A00] text-[#008A00] bg-[#008A00]/5" : "border-border text-muted-foreground"}`}
                    >
                      {h.active ? "Active" : "Paused"}
                    </button>
                  </div>
                  <div className="mt-1.5 flex items-center gap-1">
                    <LinkSimple size={12} className="text-muted-foreground shrink-0" />
                    <code className="font-mono text-xs text-muted-foreground truncate">{h.url}</code>
                    <CopyInline text={h.url} />
                  </div>
                  <div className="mt-2 flex items-center gap-3 flex-wrap text-[11px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Clock size={11} /> {h.hit_count || 0} sent · {h.success_count || 0} OK · {h.failure_count || 0} failed
                    </span>
                    <span>Events: {h.events.join(", ")}</span>
                    {h.last_failure_reason && (
                      <span className="inline-flex items-center gap-1 text-[#E00000]">
                        <Warning size={11} /> {h.last_failure_reason.slice(0, 60)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <IconBtn onClick={() => test(h)} title="Send test event" testid={`test-${h.id}`}>
                    <Play size={14} />
                  </IconBtn>
                  <IconBtn onClick={() => showDeliveries(h)} title="View deliveries" testid={`log-${h.id}`}>
                    <Clock size={14} />
                  </IconBtn>
                  <IconBtn onClick={() => { setEditing(h); setModalOpen(true); }} title="Edit" testid={`edit-${h.id}`}>
                    <PencilSimple size={14} />
                  </IconBtn>
                  <IconBtn onClick={() => del(h)} title="Delete" testid={`delete-${h.id}`} danger>
                    <Trash size={14} />
                  </IconBtn>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <WebhookModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        editing={editing}
        allEvents={allEvents}
        presets={presets}
        onSaved={load}
      />
      <DeliveriesModal
        open={!!deliveriesHook}
        onClose={() => setDeliveriesHook(null)}
        hook={deliveriesHook}
      />
    </div>
  );
}
